import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';
import { Gauge, ArrowRight, Search, X, Loader, Scale, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { addVehicleToCompare } from '../components/VehicleComparison';
import VehicleComparison from '../components/VehicleComparison';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import { isCallForPrice } from '../utils/priceDisplay';
import { searchVehicles } from '../utils/vehicleSearch';
import { InventorySEO } from '../components/SEOComponents';
import { track } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INVENTORY_CACHE_KEY = 'bcm_vehicles_v3';

/* ── Vehicle Card ── */
const VehicleCard = ({ vehicle }) => {
  const images = vehicle.images || [];
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [addedToCompare, setAddedToCompare] = useState(false);
  const fallbackImage = '/photos-coming-soon.jpg';

  const getImageUrl = (url) => {
    if (!url) return fallbackImage;
    return resolveImageUrl(url) || fallbackImage;
  };

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const added = addVehicleToCompare(vehicle);
    if (added) { setAddedToCompare(true); setTimeout(() => setAddedToCompare(false), 2000); }
  };

  const handleImageError = () => {
    // If current image fails, try the next one
    if (currentImgIndex < images.length - 1) {
      setCurrentImgIndex(prev => prev + 1);
    } else {
      // No more images to try
      setImageError(true);
    }
  };

  const imageSrc = imageError || !images[currentImgIndex] ? fallbackImage : getImageUrl(images[currentImgIndex]);

  const isSold = vehicle.status === 'Sold';

  return (
    <Link to={`/vehicle/${vehicle.id}`} className="block h-full" data-testid={`vehicle-card-${vehicle.id}`}>
      <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden h-full flex flex-col group hover:shadow-xl transition-shadow duration-300 ${isSold ? 'opacity-90' : ''}`}>
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden bg-gray-100 flex-shrink-0">
          <img src={imageSrc} alt={vehicle.title} className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${isSold ? 'grayscale-[30%]' : ''}`} onError={handleImageError} loading="lazy" decoding="async" width="400" height="300" />
          {/* SOLD banner */}
          {isSold && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-red-600 text-white px-4 sm:px-8 py-1 sm:py-2 text-base sm:text-2xl font-black uppercase tracking-widest rotate-[-20deg] shadow-lg">
                SOLD
              </div>
            </div>
          )}
          {/* Badges */}
          <div className="absolute top-1.5 left-1.5 flex gap-1 flex-wrap">
            {vehicle.badges && vehicle.badges.filter(b => b !== 'Sold').map((badge, i) => (
              <span key={i} className={`px-1.5 sm:px-2.5 py-0.5 sm:py-1 text-[9px] sm:text-[11px] font-bold uppercase tracking-wide rounded ${
                badge === 'Deal Pending' ? 'bg-amber-500 text-white' :
                badge === 'Low km' || badge === 'LOW KM' ? 'bg-gray-800 text-white' : 'bg-red-600 text-white'
              }`}>{badge}</span>
            ))}
          </div>
          {/* Compare */}
          {!isSold && (
            <button onClick={handleCompare}
              className={`absolute top-1.5 right-1.5 w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all ${addedToCompare ? 'bg-green-600 text-white' : 'bg-white/90 text-gray-600 hover:bg-black hover:text-white'}`}
              title="Add to compare" data-testid={`compare-btn-${vehicle.id}`}>
              <Scale className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-2.5 sm:p-4 flex flex-col flex-grow">
          <h3 className="text-xs sm:text-[15px] font-bold text-gray-900 mb-0.5 sm:mb-1 leading-snug min-h-[2rem] sm:min-h-[2.5rem] line-clamp-2">
            {vehicle.title}{vehicle.trim && !vehicle.title?.includes(vehicle.trim) ? ` ${vehicle.trim}` : ''}
          </h3>
          {!isSold ? (
            <p className="mb-1.5 sm:mb-3">
              <span className="text-red-600 text-sm sm:text-xl font-bold">{isCallForPrice(vehicle) ? 'Call for price' : `$${Number(vehicle.price).toLocaleString()}`}</span>
              {!isCallForPrice(vehicle) && <span className="text-gray-400 text-[10px] sm:text-xs ml-1 sm:ml-2">excl. taxes</span>}
            </p>
          ) : (
            <p className="mb-1.5 sm:mb-3">
              <span className="text-gray-500 text-sm sm:text-lg font-bold">SOLD</span>
            </p>
          )}
          {/* Specs row */}
          <div className="flex items-center gap-2 text-[10px] sm:text-xs text-gray-500 mb-2 sm:mb-4 flex-wrap">
            <span className="flex items-center gap-1">
              <Gauge className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400" />
              {(vehicle.km || vehicle.mileage || 0).toLocaleString()} km
            </span>
          </div>
          {/* CTA */}
          <div className={`mt-auto w-full text-white text-center rounded py-1.5 sm:py-2.5 font-semibold text-xs sm:text-sm transition-colors cursor-pointer ${isSold ? 'bg-gray-600' : 'bg-red-600 hover:bg-red-700'}`} data-testid={`view-details-${vehicle.id}`}>
            View Details
          </div>
        </div>
      </div>
    </Link>
  );
};

/* ── Checkbox Filter Section ── */
const FilterSection = ({ title, items, selected, onChange, testIdPrefix }) => {
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const visibleItems = showAll ? items : items.slice(0, 8);
  const hasMore = items.length > 8;

  return (
    <div className="border-b border-gray-200 pb-4 mb-4">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center justify-between w-full text-left mb-2" data-testid={`filter-section-${testIdPrefix}`}>
        <h4 className="font-bold text-gray-900 text-sm">{title}</h4>
        {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {expanded && (
        <div className="space-y-1.5">
          {visibleItems.map(item => (
            <label key={item.value} className="flex items-center gap-2 cursor-pointer group py-0.5" data-testid={`filter-${testIdPrefix}-${item.value}`}>
              <input
                type="checkbox"
                checked={selected.includes(item.value)}
                onChange={() => onChange(item.value)}
                className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
              />
              <span className="text-sm text-gray-700 group-hover:text-gray-900">{item.label}</span>
              <span className="text-xs text-gray-400 ml-auto">({item.count})</span>
            </label>
          ))}
          {hasMore && (
            <button onClick={() => setShowAll(!showAll)} className="text-xs text-red-600 hover:text-red-800 font-medium mt-1">
              {showAll ? 'Show less' : `+ ${items.length - 8} more`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Main Page ── */
const ITEMS_PER_PAGE = 16;

// Per-category page copy + SEO for the dedicated /vehicles, /powersports, /marine pages.
const CATEGORY_META = {
  'Vehicles': {
    eyebrow: 'Cars · Trucks · SUVs',
    title: 'Vehicles for Sale',
    subtitle: 'Browse our handpicked selection of quality cars, trucks, and SUVs.',
    docTitle: 'Vehicles for Sale in Winnipeg | Big Chief Motors',
    path: '/vehicles',
  },
  'Power Sports': {
    eyebrow: 'ATV · UTV · Snowmobile · Motorcycle',
    title: 'Power Sports for Sale',
    subtitle: 'ATVs, UTVs, snowmobiles, side-by-sides and more — built for the trail and the snow.',
    docTitle: 'Power Sports — ATVs, UTVs & Snowmobiles in Winnipeg | Big Chief Motors',
    path: '/powersports',
  },
  'Marine': {
    eyebrow: 'Boats · Pontoons · Watercraft',
    title: 'Marine for Sale',
    subtitle: 'Boats, pontoons, and personal watercraft — get out on the water this season.',
    docTitle: 'Marine — Boats & Watercraft for Sale in Winnipeg | Big Chief Motors',
    path: '/marine',
  },
};

const InventoryPage = ({ lockedCategory = null }) => {
  const catMeta = lockedCategory ? CATEGORY_META[lockedCategory] : null;
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  // Seed the search box from a ?search= query param (e.g. from the homepage hero search).
  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('search') || '';
  });
  const [sortBy, setSortBy] = useState('newest');
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [selectedMakes, setSelectedMakes] = useState([]);
  const [selectedBodyTypes, setSelectedBodyTypes] = useState([]);
  const [selectedDrivetrains, setSelectedDrivetrains] = useState([]);
  const [selectedTransmissions, setSelectedTransmissions] = useState([]);
  // When the page is locked to a category (dedicated route), pre-select it.
  const [selectedCategories, setSelectedCategories] = useState(lockedCategory ? [lockedCategory] : []);
  const [selectedYears, setSelectedYears] = useState([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    document.title = catMeta ? catMeta.docTitle : 'Vehicle Inventory | Big Chief Motors';
  }, [catMeta]);

  useEffect(() => { fetchVehicles(); }, []);

  const fetchVehicles = async () => {
    let hasCachedInventory = false;

    try {
      // Load from localStorage cache first for instant display
      try {
        const cached = localStorage.getItem(INVENTORY_CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setVehicles(parsed);
            hasCachedInventory = true;
          }
        }
      } catch (e) {}
      
      const response = await axios.get(`${API}/vehicles`);
      const publicVehicles = Array.isArray(response.data) ? response.data : [];

      if (publicVehicles.length > 0) {
        setVehicles(publicVehicles);
        try { localStorage.setItem(INVENTORY_CACHE_KEY, JSON.stringify(publicVehicles)); } catch (e) {}
      } else {
        setVehicles([]);
        try { localStorage.removeItem(INVENTORY_CACHE_KEY); } catch (e) {}
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      if (!hasCachedInventory) setVehicles([]);
    } finally { setLoading(false); }
  };

  const getCreatedAtTimestamp = (vehicle) => {
    const createdAt = vehicle?.created_at ? new Date(vehicle.created_at).getTime() : 0;
    return Number.isFinite(createdAt) ? createdAt : 0;
  };

  // Toggle helper (records the filter the visitor turned on; never fires on mount)
  const toggleFilter = (setter, type, selected) => (value) => {
    if (type && !selected.includes(value)) track('filter', { type, value });
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  // Determine vehicle category
  const getCategory = (v) => {
    if (v.category === 'Power Sports' || ['Snowmobile','ATV','UTV','Motorcycle','Dirt Bike','Side-by-Side'].includes(v.bodyType)) return 'Power Sports';
    if (v.category === 'Marine' || ['Boat','Personal Watercraft','Pontoon','Fishing Boat','Jet Ski'].includes(v.bodyType)) return 'Marine';
    return 'Vehicles';
  };

  // Normalize make name to title case for consistent grouping
  const normalizeMake = (make) => {
    if (!make) return '';
    return make.trim().toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };

  // Build filter options with counts from ALL vehicles (before filtering)
  const filterOptions = useMemo(() => {
    const conditions = {};
    const makes = {};
    const bodyTypes = {};
    const drivetrains = {};
    const transmissions = {};
    const categories = {};
    const years = {};

    vehicles.forEach(v => {
      // Condition
      const cond = v.condition || 'Used';
      conditions[cond] = (conditions[cond] || 0) + 1;

      // Make (normalized)
      if (v.make) {
        const normalized = normalizeMake(v.make);
        makes[normalized] = (makes[normalized] || 0) + 1;
      }

      // Body type
      if (v.bodyType) bodyTypes[v.bodyType] = (bodyTypes[v.bodyType] || 0) + 1;

      // Drivetrain
      if (v.drivetrain) drivetrains[v.drivetrain] = (drivetrains[v.drivetrain] || 0) + 1;

      // Transmission
      if (v.transmission) transmissions[v.transmission] = (transmissions[v.transmission] || 0) + 1;

      // Category
      const cat = getCategory(v);
      categories[cat] = (categories[cat] || 0) + 1;

      // Year
      if (v.year) {
        const yr = String(v.year);
        years[yr] = (years[yr] || 0) + 1;
      }
    });

    const toItems = (obj) => Object.entries(obj).sort((a, b) => a[0].localeCompare(b[0])).map(([k, c]) => ({ value: k, label: k, count: c }));
    const yearItems = Object.entries(years).sort((a, b) => Number(b[0]) - Number(a[0])).map(([k, c]) => ({ value: k, label: k, count: c }));

    return {
      conditions: toItems(conditions),
      makes: toItems(makes),
      bodyTypes: toItems(bodyTypes),
      drivetrains: toItems(drivetrains),
      transmissions: toItems(transmissions),
      categories: toItems(categories),
      years: yearItems,
    };
  }, [vehicles]);

  // Filters first, then smart search (utils/vehicleSearch) over what's left.
  const searchResult = useMemo(() => {
    const base = vehicles.filter(v => {
      const matchesCondition = selectedConditions.length === 0 || selectedConditions.includes(v.condition || 'Used');
      const matchesMake = selectedMakes.length === 0 || selectedMakes.includes(normalizeMake(v.make));
      const matchesBody = selectedBodyTypes.length === 0 || selectedBodyTypes.includes(v.bodyType);
      const matchesDrivetrain = selectedDrivetrains.length === 0 || selectedDrivetrains.includes(v.drivetrain);
      const matchesTransmission = selectedTransmissions.length === 0 || selectedTransmissions.includes(v.transmission);
      const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(getCategory(v));
      const matchesYear = selectedYears.length === 0 || selectedYears.includes(String(v.year));
      return matchesCondition && matchesMake && matchesBody && matchesDrivetrain && matchesTransmission && matchesCategory && matchesYear;
    });
    return searchVehicles(base, searchTerm);
  }, [vehicles, searchTerm, selectedConditions, selectedMakes, selectedBodyTypes, selectedDrivetrains, selectedTransmissions, selectedCategories, selectedYears]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredVehicles = useMemo(() => {
    if (!searchResult.exact) return [];
    const result = [...searchResult.results];
    // While searching, keep best-match order unless the shopper picked a sort themselves.
    const byRelevance = searchTerm.trim() && sortBy === 'newest';
    const rank = new Map(result.map((v, i) => [v.id, i]));

    result.sort((a, b) => {
      const aIsSold = a.status === 'Sold' ? 1 : 0;
      const bIsSold = b.status === 'Sold' ? 1 : 0;

      if (aIsSold !== bIsSold) {
        return aIsSold - bIsSold;
      }

      if (byRelevance) return rank.get(a.id) - rank.get(b.id);
      if (sortBy === 'price-low') return (a.price || 0) - (b.price || 0);
      if (sortBy === 'price-high') return (b.price || 0) - (a.price || 0);
      if (sortBy === 'km-low') return (a.km || a.mileage || 0) - (b.km || b.mileage || 0);
      if (sortBy === 'newest') return getCreatedAtTimestamp(b) - getCreatedAtTimestamp(a);
      if (sortBy === 'oldest') return getCreatedAtTimestamp(a) - getCreatedAtTimestamp(b);

      return 0;
    });

    return result;
  }, [searchResult, searchTerm, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // No exact match: show the closest units instead of a dead end.
  const closestVehicles = searchResult.exact ? [] : searchResult.results;

  // One-tap body type chips (counts within this page's category).
  const typeChips = useMemo(() => {
    const counts = {};
    vehicles.forEach(v => {
      if (v.bodyType && (!lockedCategory || getCategory(v) === lockedCategory)) counts[v.bodyType] = (counts[v.bodyType] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([type, count]) => ({ type, count, label: /s$/i.test(type) ? type : `${type}s` }));
  }, [vehicles, lockedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset to page 1 when filters/search/sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, selectedConditions, selectedMakes, selectedBodyTypes, selectedDrivetrains, selectedTransmissions, selectedCategories, selectedYears]);

  // Search analytics: once typing settles (~800ms) and on first load with ?search=
  // (covers the homepage hero search). results = 0 is the "demand we don't stock" signal.
  const resultCountRef = useRef(0);
  resultCountRef.current = filteredVehicles.length;
  const lastSearchRef = useRef('');
  useEffect(() => {
    const q = searchTerm.trim().slice(0, 100);
    if (!q || loading || vehicles.length === 0 || q === lastSearchRef.current) return undefined;
    const t = setTimeout(() => {
      lastSearchRef.current = q;
      track('search', { q, results: resultCountRef.current });
    }, 800);
    return () => clearTimeout(t);
  }, [searchTerm, loading, vehicles.length]);

  // Pagination
  const totalPages = Math.ceil(filteredVehicles.length / ITEMS_PER_PAGE);
  const paginatedVehicles = filteredVehicles.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const clearAllFilters = () => {
    setSearchTerm('');
    setSortBy('newest');
    setSelectedConditions([]);
    setSelectedMakes([]);
    setSelectedBodyTypes([]);
    setSelectedDrivetrains([]);
    setSelectedTransmissions([]);
    setSelectedCategories(lockedCategory ? [lockedCategory] : []);
    setSelectedYears([]);
    setCurrentPage(1);
  };

  const activeFilterCount = selectedConditions.length + selectedMakes.length + selectedBodyTypes.length + selectedDrivetrains.length + selectedTransmissions.length + selectedCategories.length + selectedYears.length + (searchTerm ? 1 : 0);

  /* ── Sidebar Content (shared between desktop & mobile) ── */
  const SidebarContent = () => (
    <>
      {/* Sort */}
      <div className="mb-4">
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
          className="w-full p-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          data-testid="sort-select">
          <option value="newest">Sort: Newest First</option>
          <option value="oldest">Sort: Oldest First</option>
          <option value="price-low">Sort: Price Low → High</option>
          <option value="price-high">Sort: Price High → Low</option>
          <option value="km-low">Sort: Lowest Mileage</option>
        </select>
      </div>

      {/* Search (desktop; phones use the box above the Filters button) */}
      <div className="relative mb-3 hidden lg:block">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" placeholder="Search make, model, SUV, truck…"
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-8 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          data-testid="search-input" />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Results count */}
      <p className="text-xs font-semibold text-gray-700 mb-4" data-testid="results-count">
        Showing {paginatedVehicles.length} of {filteredVehicles.length} results
      </p>

      {/* Filter heading + reset */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900 text-base">Filter Inventory</h3>
        {activeFilterCount > 0 && (
          <button onClick={clearAllFilters} className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium" data-testid="clear-filters-btn">
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>

      {/* Category */}
      {!lockedCategory && (
        <FilterSection title="Category" items={filterOptions.categories} selected={selectedCategories} onChange={toggleFilter(setSelectedCategories, 'category', selectedCategories)} testIdPrefix="category" />
      )}

      {/* Condition */}
      <FilterSection title="Condition" items={filterOptions.conditions} selected={selectedConditions} onChange={toggleFilter(setSelectedConditions, 'condition', selectedConditions)} testIdPrefix="condition" />

      {/* Make & Model */}
      <FilterSection title="Make & Model" items={filterOptions.makes} selected={selectedMakes} onChange={toggleFilter(setSelectedMakes, 'make', selectedMakes)} testIdPrefix="make" />

      {/* Body Type */}
      <FilterSection title="Body Type" items={filterOptions.bodyTypes} selected={selectedBodyTypes} onChange={toggleFilter(setSelectedBodyTypes, 'body', selectedBodyTypes)} testIdPrefix="bodytype" />

      {/* Year */}
      <FilterSection title="Year" items={filterOptions.years} selected={selectedYears} onChange={toggleFilter(setSelectedYears, 'year', selectedYears)} testIdPrefix="year" />

      {/* Transmission */}
      <FilterSection title="Transmission" items={filterOptions.transmissions} selected={selectedTransmissions} onChange={toggleFilter(setSelectedTransmissions, 'transmission', selectedTransmissions)} testIdPrefix="transmission" />

      {/* Drivetrain */}
      <FilterSection title="Drivetrain" items={filterOptions.drivetrains} selected={selectedDrivetrains} onChange={toggleFilter(setSelectedDrivetrains, 'drivetrain', selectedDrivetrains)} testIdPrefix="drivetrain" />
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <InventorySEO
        baseUrl={window.location.origin}
        vehicleCount={vehicles.length}
        title={catMeta ? catMeta.docTitle : undefined}
        description={catMeta ? catMeta.subtitle : undefined}
        path={catMeta ? catMeta.path : '/inventory'}
      />
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-red-600"></span>
            <span className="text-red-600 font-semibold text-xs uppercase tracking-[0.22em]">{catMeta ? catMeta.eyebrow : 'Our Inventory'}</span>
          </div>
          <h1 className="font-display font-extrabold tracking-tight text-4xl md:text-6xl text-gray-900" data-testid="inventory-heading">
            {catMeta ? catMeta.title : 'Browse Vehicles for Sale'}
          </h1>
          {catMeta && <p className="mt-4 text-lg text-gray-600 max-w-2xl">{catMeta.subtitle}</p>}
        </div>

        {/* Mobile search — always visible */}
        <div className="lg:hidden relative mb-3">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="search" inputMode="search" enterKeyHint="search" placeholder="Search make, model, SUV, truck…"
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search inventory"
            className="w-full pl-11 pr-10 py-3 bg-white border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-red-500 [&::-webkit-search-cancel-button]:hidden"
            data-testid="mobile-search-input" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Mobile filter toggle */}
        <div className="lg:hidden mb-4">
          <button onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
            className="w-full flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm font-semibold text-gray-800"
            data-testid="mobile-filter-toggle">
            <span>Filters {activeFilterCount > 0 && `(${activeFilterCount})`}</span>
            {mobileFiltersOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {mobileFiltersOpen && (
            <div className="mt-2 bg-white border border-gray-200 rounded-lg p-4">
              {SidebarContent()}
            </div>
          )}
        </div>

        {/* Two-column layout */}
        <div className="flex gap-8">
          {/* LEFT SIDEBAR - Desktop */}
          <aside className="hidden lg:block w-[240px] flex-shrink-0" data-testid="filter-sidebar">
            <div className="sticky top-4">
              {SidebarContent()}
            </div>
          </aside>

          {/* RIGHT - Vehicle Grid */}
          <div className="flex-1 min-w-0">
            {!loading && typeChips.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-3 mb-1 -mx-1 px-1" role="group" aria-label="Filter by vehicle type" data-testid="type-chips">
                {[{ type: null, label: 'All', count: null }, ...typeChips].map(({ type, label, count }) => {
                  const active = type ? selectedBodyTypes.length === 1 && selectedBodyTypes[0] === type : selectedBodyTypes.length === 0;
                  return (
                    <button key={label} type="button" aria-pressed={active}
                      onClick={() => {
                        if (type && !active) track('filter', { type: 'body_chip', value: type });
                        setSelectedBodyTypes(type && !active ? [type] : []);
                      }}
                      className={`flex-shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${
                        active ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:ring-gray-400'
                      }`}>
                      {label}{count != null && <span className={`ml-1.5 text-xs ${active ? 'text-gray-300' : 'text-gray-400'}`}>{count}</span>}
                    </button>
                  );
                })}
              </div>
            )}
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-lg overflow-hidden animate-pulse">
                    <div className="aspect-[4/3] bg-gray-200" />
                    <div className="p-2.5 sm:p-4 space-y-2 sm:space-y-3">
                      <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-4 sm:h-6 bg-gray-200 rounded w-1/3" />
                      <div className="h-2.5 sm:h-3 bg-gray-200 rounded w-1/2" />
                      <div className="h-8 sm:h-10 bg-gray-200 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredVehicles.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-testid="vehicle-grid">
                  {paginatedVehicles.map((vehicle) => (
                    <VehicleCard key={vehicle.id} vehicle={vehicle} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
                    <button
                      onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm rounded-lg border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      Previous
                    </button>
                    {(() => {
                      // Windowed pagination: first, last, current ±1, with ellipses.
                      const pages = [];
                      for (let p = 1; p <= totalPages; p++) {
                        if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
                          pages.push(p);
                        } else if (pages[pages.length - 1] !== '…') {
                          pages.push('…');
                        }
                      }
                      return pages.map((page, idx) =>
                        page === '…' ? (
                          <span key={`ellipsis-${idx}`} className="w-10 h-10 flex items-center justify-center text-gray-400 select-none">…</span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => { setCurrentPage(page); window.scrollTo(0, 0); }}
                            className={`w-10 h-10 text-sm rounded-lg font-medium transition-colors ${
                              page === currentPage
                                ? 'bg-red-600 text-white'
                                : 'border border-gray-300 hover:bg-gray-100'
                            }`}
                            data-testid={`page-${page}`}
                          >
                            {page}
                          </button>
                        )
                      );
                    })()}
                    <button
                      onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo(0, 0); }}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm rounded-lg border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div data-testid="no-exact-results">
                <div className="bg-white rounded-lg border border-gray-200 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
                  <div className="flex-1">
                    <p className="text-gray-900 font-semibold text-lg">
                      {searchTerm.trim() ? <>No exact match for &ldquo;{searchTerm.trim()}&rdquo; right now</> : 'No vehicles match these filters'}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {closestVehicles.length > 0 ? 'Here are the closest units we have. ' : ''}
                      Can&apos;t find it? Tell us what you want — we source vehicles for customers every week.
                    </p>
                  </div>
                  <div className="flex flex-col sm:items-end gap-2">
                    <Link to={`/contact${searchTerm.trim() ? `?vehicle=${encodeURIComponent(searchTerm.trim())}` : ''}`}
                      className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-semibold text-sm transition whitespace-nowrap"
                      data-testid="find-it-cta">
                      We&apos;ll find it for you <ArrowRight size={16} />
                    </Link>
                    <button onClick={clearAllFilters} className="inline-flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-gray-800" data-testid="empty-clear-filters">
                      <RotateCcw size={13} /> Clear search &amp; filters
                    </button>
                  </div>
                </div>
                {closestVehicles.length > 0 && (
                  <>
                    <h2 className="mt-8 mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500">Closest matches</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-testid="closest-grid">
                      {closestVehicles.map((vehicle) => <VehicleCard key={vehicle.id} vehicle={vehicle} />)}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Can't find what you're looking for? CTA - below inventory */}
        {!loading && vehicles.length > 0 && (
          <div className="mt-12 bg-gray-900 rounded-2xl ring-1 ring-white/10 shadow-sm p-8 flex flex-col md:flex-row items-center gap-6" data-testid="inventory-cta-banner">
            <div className="flex-1">
              <h3 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-white mb-2">Can't Find What You're Looking For?</h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                If you don't see the exact unit you're looking for, don't worry — just reach out. We have access to a wide network of suppliers and can source the vehicle, ATV, snowmobile, boat, or RV you want. Tell us what you're after, and we'll make it happen.
              </p>
            </div>
            <Link to="/contact" className="flex-shrink-0 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-7 py-3.5 rounded-full font-semibold text-sm transition whitespace-nowrap" data-testid="inventory-contact-cta">
              Contact Us <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </main>

      <Footer />
      <VehicleComparison />
    </div>
  );
};

export default InventoryPage;
